import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BotonPreguntaleAtlas } from "../../chat-atlas";
import { useBrandProfile } from "../../hooks/useBrandProfile";
import { BannerVistaEjemplo } from "./components/BannerVistaEjemplo";
import { PanelConfigMapa } from "./components/PanelConfigMapa";
import { useConfigMapa } from "./datos/configMapa";
import { PROPUESTAS_EJEMPLO } from "./datos/ejemplo";
import { useFuentesConectadas } from "./datos/fuentesConectadas";
import { estadoDeFuente, estadoNodoDeFuente } from "./datos/estadoFuente";
import { ANILLO, ARISTAS, NODOS, VIEWBOX, type EstadoNodo, type NodoGrafo } from "./grafo/definicion";
import { grafoSegunConfig } from "./grafo/grafoSegunConfig";
import { CircuitoMapa } from "./grafo/CircuitoMapa";

/**
 * Pantalla estrella del Estudio de marketing: el circuito de agentes.
 *
 * Entra ENTERA en una pantalla, sin scroll: el mapa se dimensiona por ALTO
 * (ver `.mapa-encaje` / `.mapa-lienzo` en index.css) y el encabezado va
 * superpuesto al lienzo en vez de apilado arriba. Lo único que queda en flujo
 * arriba del mapa es el banner de vista de ejemplo.
 *
 * El centro del anillo nunca queda en blanco — tiene dos caras y la elige el
 * dato, no el diseño: si hay propuestas esperando, el número y la acción; si
 * no hay ninguna, qué falta conectar (en el Bloque A siempre es algo).
 *
 * Las tres fuentes (Instagram, Tiendanube, Meta Ads) son el primer dato real
 * de esta sección: `useFuentesConectadas` trae el estado real de conexión y
 * acá se aplica sobre una COPIA de `NODOS` — nunca mutando el array
 * importado, que es output aprobado de otra tarea y lo consume también
 * `CircuitoMapa` fuera de esta pantalla.
 *
 * El grafo que se dibuja NO es `NODOS`/`ARISTAS` directo: es lo que devuelve
 * `grafoSegunConfig` a partir de la configuración de la marca activa (dos
 * interruptores que funden nodos y recierran el circuito). Esta pantalla no
 * sabe qué cambia cada opción; pasa la config y dibuja lo que le devuelven
 * (ADR-2026-08-17-mapa-configuracion-y-grafo-derivado).
 */

const pendientes = PROPUESTAS_EJEMPLO.filter(
  (p) => p.estado === "propuesta" || p.estado === "listo",
).length;

const CENTRO =
  pendientes > 0
    ? {
        numero: String(pendientes),
        bajada: "propuestas esperando",
        cta: "Ver el plan",
        ruta: "/estudio-marketing/plan",
      }
    : {
        numero: "—",
        bajada: "todo al día. Falta conectar Meta Ads",
        cta: "Ver qué falta",
        ruta: "/estudio-marketing/insights",
      };

// El bloque de estado se posiciona en coordenadas del viewBox del circuito,
// como porcentaje del ancho/alto — así queda centrado en el anillo (560,240)
// sin importar a qué ancho renderice el SVG.
const [, , anchoViewBox, altoViewBox] = VIEWBOX.split(" ").map(Number);

export const MapaPage = () => {
  const navigate = useNavigate();
  const { profile, currentBrandId, loading, loadingBrands } = useBrandProfile();
  const fuentes = useFuentesConectadas(profile?.id_company ?? null, currentBrandId);
  // El mapa tiene que contestar lo MISMO que la pantalla de datos, que está a
  // un clic (los tres nodos de fuente llevan ahí). Por eso mira las dos cosas
  // que ella mira: si el perfil todavía carga, y si el perfil FALLÓ — sin
  // `id_company` no se consulta la conexión de Instagram, así que no hay error que
  // reportar y el booleano queda en `false` por omisión, que es "sin conectar"
  // sin haber preguntado nada (ADR-2026-08-17-estudio-marketing-fixes-revision).
  const cargandoPerfil = loading || loadingBrands;
  const perfilFallo = !cargandoPerfil && profile === null;
  const cargandoFuentes = cargandoPerfil || fuentes.cargando;
  // La config es POR MARCA: si `currentBrandId` cambia, el hook levanta la de
  // la marca nueva (y mientras no hay marca, los valores por defecto).
  const [configMapa, guardarConfigMapa] = useConfigMapa(currentBrandId);

  // El grafo derivado. `grafoSegunConfig` es pura y devuelve arrays nuevos, así
  // que memorizarla contra la config alcanza: mientras nadie toque un
  // interruptor, `CircuitoMapa` recibe las mismas aristas por identidad.
  const grafo = useMemo(() => grafoSegunConfig(NODOS, ARISTAS, configMapa), [configMapa]);

  // Copia con el estado real de las tres fuentes — nunca se toca el array
  // importado. El resto de los nodos queda tal cual salió del grafo derivado.
  //
  // El estado NO se deriva acá con un ternario sobre el booleano: sale de
  // `estadoDeFuente` + `estadoNodoDeFuente`, las mismas dos funciones que usa
  // la pantalla de datos. Antes el mapa pintaba de "sin conectar" una consulta
  // caída —una afirmación sobre la cuenta hecha sin ningún dato— y el nodo
  // lleva justamente a la pantalla que en ese caso dice "No pudimos
  // consultarlo": dos respuestas distintas a la misma pregunta con un clic de
  // distancia (ADR-2026-08-17-estudio-marketing-fixes-revision).
  const estadoNodo = (conectado: boolean, fallo: boolean): EstadoNodo =>
    estadoNodoDeFuente(
      estadoDeFuente({ cargando: cargandoFuentes, conectado, fallo: fallo || perfilFallo }),
    );

  const nodosConFuentesReales: NodoGrafo[] = grafo.nodos.map((nodo) => {
    if (nodo.id === "instagram") {
      return { ...nodo, estado: estadoNodo(fuentes.instagram, fuentes.falloInstagram) };
    }
    if (nodo.id === "tiendanube") {
      return { ...nodo, estado: estadoNodo(fuentes.tiendanube, fuentes.falloTiendanube) };
    }
    if (nodo.id === "meta_ads") {
      // No hay integración: no hay consulta que pueda fallar ni que esté en
      // vuelo. Es "sin conectar" de verdad, no "no sabemos".
      return { ...nodo, estado: fuentes.meta_ads ? "activo" : "sin-conectar" };
    }
    return nodo;
  });

  const fuentesConectadasCount =
    Number(fuentes.instagram) + Number(fuentes.tiendanube) + Number(fuentes.meta_ads);
  // Si alguna consulta se cayó, el conteo no es un dato: "0 de 3" afirmaría que
  // ninguna está conectada cuando lo único cierto es que no se pudo preguntar.
  const algunaFallo = fuentes.falloInstagram || fuentes.falloTiendanube || perfilFallo;

  return (
    // Alto fijo al viewport: en desktop el panel de contenido es `fixed` de
    // top-0 a bottom-0 (`.content-panel-clip`, ADR-171), así que 100dvh es
    // exactamente el alto disponible y la pantalla no scrollea. `h-full` no
    // serviría: el layout de la sección mete en el medio un `<div>` de alto
    // automático, contra el que un porcentaje no resuelve.
    <div className="mapa-pantalla flex h-[100dvh] flex-col overflow-hidden p-8">
      {/* Esta pantalla mezcla las dos cosas a centímetros de distancia: la
          píldora de fuentes es dato real de la cuenta, pero el centro del
          anillo sale de `PROPUESTAS_EJEMPLO`. Sin el cartel no hay forma de
          saber cuál es cuál. El banner es lo único que queda ARRIBA del mapa:
          es un aviso y tiene que verse antes que nada. */}
      {/* El botón de Atlas va EN LA MISMA FILA que el banner, no flotando sobre
          el lienzo ni apilado arriba. Los dos motivos son el encaje: apilado
          sumaría una fila entera de alto y el mapa —que entra por ALTO— se
          achicaría; superpuesto taparía nodos (el lienzo conserva la relación
          del viewBox, así que a ciertos anchos no sobra margen en ninguna
          esquina). En esta fila el botón es más bajo que el banner, así que el
          alto disponible para el mapa no cambia ni un pixel. */}
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <BannerVistaEjemplo />
        </div>
        <BotonPreguntaleAtlas
          pantalla="Mapa del estudio de marketing"
          detalle="El circuito de agentes de la marca: fuentes conectadas, análisis y generación de contenido."
        />
      </div>

      {/* El área libre que queda debajo del banner. `.mapa-encaje` la declara
          container de tamaño para que el lienzo se mida contra SU ALTO. */}
      <div className="mapa-encaje mt-4 flex min-h-0 flex-1 items-center justify-center">
        <div
          className="mapa-lienzo relative"
          style={{
            aspectRatio: `${anchoViewBox} / ${altoViewBox}`,
            // El mapa entra por ALTO: el ancho es el menor entre "todo el
            // ancho disponible" y "el que le corresponde al alto disponible".
            // La caja conserva EXACTAMENTE la relación del viewBox — es lo que
            // sostiene el posicionado en % del bloque del centro, que se
            // despegaría del anillo si el SVG quedara letterboxed adentro.
            width: `min(100%, calc(100cqh * ${anchoViewBox} / ${altoViewBox}))`,
          }}
        >
          <CircuitoMapa nodos={nodosConFuentesReales} aristas={grafo.aristas} onNavegar={navigate} />

          {/* Encabezado SUPERPUESTO al lienzo: apilado arriba se comía el alto
              vertical que el mapa necesita. Va en las esquinas de arriba, que
              es donde el grafo no tiene nada: el nodo más cercano al título es
              `perfil` (y=55 el borde de arriba de su círculo) y el más cercano
              a la píldora es `vos` (y=124). Los tamaños escalan con el mapa
              (`cqw`, ver index.css) justamente para que esa holgura no dependa
              de a qué tamaño se dibuje — pero ACOTADOS con `clamp`: la píldora
              de fuentes es el único dato real de la cuenta en esta pantalla y
              en `cqw` puro caía a 5px con el lienzo angosto. `pointer-events-none`
              para no robarle el clic a los nodos que quedan debajo. */}
          <div className="mapa-encabezado pointer-events-none absolute inset-0 flex items-start justify-between gap-4">
            <div>
              <h1 className="mapa-titulo whitespace-nowrap font-semibold text-brand-black">
                Tu equipo de marketing
              </h1>
              {/* En lugar de una bajada que explicaba el dibujo, el estado
                  del equipo: el circuito está corriendo. Mismo par verde que
                  el resto de la sección usa para "esto está bien"
                  (#DDEFE7 / #1D7A57) y mismo tamaño y padding 2:1 que la
                  píldora de fuentes, de ahí que comparta `.mapa-pildora`.
                  Sin borde: el fondo verde ya la separa del lienzo. */}
              <span className="mapa-pildora mapa-pildora-estado inline-flex items-center whitespace-nowrap rounded-full bg-[#DDEFE7] font-medium text-[#1D7A57]">
                <span className="mapa-pildora-punto rounded-full bg-[#1D7A57]" aria-hidden="true" />
                Activo
              </span>
            </div>

            {/* Mientras `cargando` es true no se dibuja nada acá — nunca un
                número provisorio (arrancar en "0 de 3" y saltar al real sería
                mentir por un instante, igual que mostrar "conectado" de más).
                Y si alguna consulta se cayó tampoco se dibuja el número: se
                dice que no se pudo consultar, que es lo único que se sabe.
                `cargandoFuentes` incluye la carga del PERFIL: mirando sólo
                `fuentes.cargando`, la consulta con `idCompany` en `null`
                terminaba antes que el perfil y la píldora alcanzaba a afirmar
                "0 de 3" antes de saber nada. */}
            {!cargandoFuentes && (
              <span
                className={`mapa-pildora shrink-0 whitespace-nowrap rounded-full font-medium ${
                  !algunaFallo && fuentesConectadasCount === 3
                    ? "bg-[#DDEFE7] text-[#1D7A57]"
                    : "bg-[#F8E3E2] text-[#C33B36]"
                }`}
              >
                {algunaFallo
                  ? "No pudimos consultar tus fuentes"
                  : `${fuentesConectadasCount} de 3 fuentes conectadas`}
              </span>
            )}
          </div>

          <div
            className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center"
            style={{
              left: `${(ANILLO.cx / anchoViewBox) * 100}%`,
              top: `${(ANILLO.cy / altoViewBox) * 100}%`,
            }}
          >
            {/* Los tamaños van en `cqw` del lienzo, igual que el encabezado y
                por el mismo motivo: en px este bloque ocupa cada vez MÁS
                coordenadas del viewBox a medida que el mapa se achica, y el
                vecino de arriba es la etiqueta de `numeros`, que termina en
                y≈152. Ver `.mapa-centro-*` en index.css. */}
            <span className="mapa-centro-numero font-semibold leading-none text-brand-black">{CENTRO.numero}</span>
            <span className="mapa-centro-bajada text-[rgb(var(--brand-black-rgb)_/_0.55)]">{CENTRO.bajada}</span>
            <button
              type="button"
              onClick={() => navigate(CENTRO.ruta)}
              className="mapa-centro-cta rounded-full bg-brand-primary font-medium text-white"
            >
              {CENTRO.cta}
            </button>
          </div>

          {/* Último del lienzo, para que el panel abierto quede por encima de
              todo lo demás (el encabezado es `inset-0` y le pasaría por
              arriba). Va en la esquina de abajo a la izquierda, la única zona
              grande sin nodos — ver `.mapa-config` en index.css. */}
          <PanelConfigMapa config={configMapa} onCambiar={guardarConfigMapa} />
        </div>
      </div>
    </div>
  );
};

export default MapaPage;
